import { useState } from 'react';
import { Plus, Settings, X, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useAppStore } from '@/stores/appStore';
import { SessionList } from '@/components/SessionList';
import { SessionStats } from '@/components/SessionStats';
import { CorpusList } from '@/components/CorpusList';
import { useCreateSession, useCorpus } from '@/hooks/useApi';
import { useNavigate } from 'react-router-dom';
import { useBreakpoint } from '@/hooks/useMediaQuery';
import { useTouchGestures } from '@/hooks/useTouchGestures';
import { cn } from '@/lib/utils';

export function MobileSidebar() {
  const { ui, toggleSidebar } = useAppStore();
  const navigate = useNavigate();
  const createSessionMutation = useCreateSession();
  const { data: corpusResponse } = useCorpus();
  const { isSmallMobile } = useBreakpoint();

  // Collapsible state - default to collapsed on mobile for better UX
  const [isSessionsCollapsed, setIsSessionsCollapsed] = useState(true);
  const [isCorpusCollapsed, setIsCorpusCollapsed] = useState(true);
  const [isStatsCollapsed, setIsStatsCollapsed] = useState(true);

  const handleNewChat = () => {
    createSessionMutation.mutate(undefined, {
      onSuccess: (session) => {
        navigate(`/chat/${session.id}`);
        toggleSidebar(); // Close sidebar after creating session
      },
    });
  };

  // Touch gestures for swipe to close
  const touchRef = useTouchGestures({
    onSwipeLeft: () => {
      if (ui.sidebarOpen) {
        toggleSidebar();
      }
    },
    threshold: 50,
  });

  return (
    <Sheet open={ui.sidebarOpen && ui.mobile} onOpenChange={toggleSidebar}>
      <SheetContent
        ref={touchRef}
        side="left"
        className={`p-0 ${isSmallMobile ? 'w-[90vw]' : 'w-80'}`}
      >
        <SheetHeader className="p-4 pb-0">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-lg font-semibold">
              Chat Sessions
            </SheetTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </SheetHeader>

        <div className="flex h-full flex-col">
          {/* New Chat Button */}
          <div className="p-4 flex-shrink-0">
            <Button
              className="w-full justify-start gap-2"
              size="sm"
              onClick={handleNewChat}
              disabled={createSessionMutation.isPending}
            >
              <Plus className="h-4 w-4" />
              New Chat
            </Button>
          </div>

          <Separator />

          {/* Sessions */}
          <div className={cn(
            "flex flex-col overflow-hidden min-h-0 transition-all duration-200",
            isSessionsCollapsed ? "flex-shrink-0" : "flex-1"
          )}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsSessionsCollapsed(!isSessionsCollapsed)}
              className="mx-2 my-1 justify-start gap-2 h-10 px-2 flex-shrink-0 hover:bg-muted/50 touch-target"
            >
              {isSessionsCollapsed ? (
                <ChevronRight className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
              <h3 className="text-sm font-medium text-muted-foreground">
                Sessions
              </h3>
            </Button>

            {!isSessionsCollapsed && (
              <div className="flex-1 overflow-hidden">
                <ScrollArea className="h-full">
                  <div className="px-2">
                    <SessionList />
                  </div>
                </ScrollArea>
              </div>
            )}
          </div>

          <Separator />

          {/* Corpus */}
          <div className={cn(
            "flex flex-col overflow-hidden min-h-0 transition-all duration-200",
            isCorpusCollapsed ? "flex-shrink-0" : "flex-1"
          )}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsCorpusCollapsed(!isCorpusCollapsed)}
              className="mx-2 my-1 justify-start gap-2 h-10 px-2 flex-shrink-0 hover:bg-muted/50 touch-target"
            >
              {isCorpusCollapsed ? (
                <ChevronRight className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
              <h3 className="text-sm font-medium text-muted-foreground">
                Corpus
                {corpusResponse?.company?.fileCount !== undefined && (
                  <span className="ml-1 text-xs">
                    ({corpusResponse.company.fileCount} file{corpusResponse.company.fileCount !== 1 ? 's' : ''})
                  </span>
                )}
              </h3>
            </Button>

            {!isCorpusCollapsed && (
              <div className="flex-1 overflow-hidden">
                <ScrollArea className="h-full">
                  <div className="px-2">
                    <CorpusList />
                  </div>
                </ScrollArea>
              </div>
            )}
          </div>

          <Separator />

          {/* Session Stats */}
          <div className="flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsStatsCollapsed(!isStatsCollapsed)}
              className="mx-2 my-1 justify-start gap-2 h-10 px-2 w-auto hover:bg-muted/50 touch-target"
            >
              {isStatsCollapsed ? (
                <ChevronRight className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
              <span className="text-sm font-medium text-muted-foreground">
                Statistics
              </span>
            </Button>

            {!isStatsCollapsed && (
              <div className="px-2 pb-2">
                <SessionStats />
              </div>
            )}
          </div>

          <Separator />

          {/* Footer */}
          <div className="p-4 flex-shrink-0">
            <Button variant="ghost" className="w-full justify-start gap-2" size="sm">
              <Settings className="h-4 w-4" />
              Manage Actions
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}